import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { UserPlus, AlertCircle, CheckCircle } from 'lucide-react';
import API_BASE_URL from '../config';

export default function Register() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const inviteToken = searchParams.get('invite');

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    email: ''
  });

  const [inviteValid, setInviteValid] = useState(null);
  const [adminInfo, setAdminInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Valida il token all'avvio
  useEffect(() => {
    if (inviteToken) {
      validateToken();
    }
  }, [inviteToken]);

  const validateToken = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/invites/validate/${inviteToken}`);
      const data = await response.json();

      if (data.valid) {
        setInviteValid(true);
        setAdminInfo(data.admin);
      } else {
        setInviteValid(false);
        setError(data.error || 'Token non valido');
      }
    } catch (err) {
      setInviteValid(false);
      setError('Errore nella validazione del token');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validazione
    if (formData.password !== formData.confirmPassword) {
      setError('Le password non corrispondono');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('La password deve essere di almeno 6 caratteri');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/mysql/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: formData.username,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          inviteToken: inviteToken
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Registrazione completata! Reindirizzamento al login...');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        setError(data.message || 'Errore durante la registrazione');
      }
    } catch (err) {
      setError('Errore di connessione al server');
    } finally {
      setLoading(false);
    }
  };

  if (inviteValid === false) {
    return (
      <div className="min-h-screen bg-light-surface dark:bg-dark-surface flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-light-surface-container dark:bg-dark-surface-container rounded-lg shadow-surface-container p-8">
          <div className="text-center">
            <AlertCircle size={64} className="mx-auto mb-4 text-light-error dark:text-dark-error" />
            <h1 className="text-2xl font-bold text-light-on-surface dark:text-dark-on-surface mb-4">
              Link di Invito Non Valido
            </h1>
            <p className="text-light-on-surface-variant dark:text-dark-on-surface-variant mb-6">
              {error}
            </p>
            <Link
              to="/"
              className="inline-block px-6 py-3 bg-light-primary dark:bg-dark-primary text-light-on-primary dark:text-dark-on-primary rounded-lg hover:opacity-90 transition-opacity"
            >
              Torna alla Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (inviteValid === null) {
    return (
      <div className="min-h-screen bg-light-surface dark:bg-dark-surface flex items-center justify-center">
        <div className="text-light-on-surface dark:text-dark-on-surface">
          Validazione del token in corso...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-light-surface dark:bg-dark-surface flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-light-surface-container dark:bg-dark-surface-container rounded-lg shadow-surface-container p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <UserPlus size={48} className="mx-auto mb-4 text-light-primary dark:text-dark-primary" />
          <h1 className="text-2xl font-bold text-light-on-surface dark:text-dark-on-surface mb-2">
            Registrazione
          </h1>
          {adminInfo && (
            <p className="text-sm text-light-on-surface-variant dark:text-dark-on-surface-variant">
              Sei stato invitato da <span className="font-semibold">{adminInfo.nome} {adminInfo.cognome}</span>
            </p>
          )}
        </div>

        {/* Messaggi */}
        {error && (
          <div className="mb-4 p-4 bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 rounded-lg flex items-start gap-2">
            <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="mb-4 p-4 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-lg flex items-start gap-2">
            <CheckCircle size={20} className="flex-shrink-0 mt-0.5" />
            <span>{success}</span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-light-on-surface dark:text-dark-on-surface mb-2">
                Nome
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 bg-light-surface dark:bg-dark-surface border border-light-outline dark:border-dark-outline rounded-lg text-light-on-surface dark:text-dark-on-surface focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-light-on-surface dark:text-dark-on-surface mb-2">
                Cognome
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 bg-light-surface dark:bg-dark-surface border border-light-outline dark:border-dark-outline rounded-lg text-light-on-surface dark:text-dark-on-surface focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-light-on-surface dark:text-dark-on-surface mb-2">
              Username
            </label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 bg-light-surface dark:bg-dark-surface border border-light-outline dark:border-dark-outline rounded-lg text-light-on-surface dark:text-dark-on-surface focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-light-on-surface dark:text-dark-on-surface mb-2">
              Email
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 bg-light-surface dark:bg-dark-surface border border-light-outline dark:border-dark-outline rounded-lg text-light-on-surface dark:text-dark-on-surface focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-light-on-surface dark:text-dark-on-surface mb-2">
              Password
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              minLength={6}
              className="w-full px-4 py-2 bg-light-surface dark:bg-dark-surface border border-light-outline dark:border-dark-outline rounded-lg text-light-on-surface dark:text-dark-on-surface focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-light-on-surface dark:text-dark-on-surface mb-2">
              Conferma Password
            </label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              minLength={6}
              className="w-full px-4 py-2 bg-light-surface dark:bg-dark-surface border border-light-outline dark:border-dark-outline rounded-lg text-light-on-surface dark:text-dark-on-surface focus:outline-none"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-light-primary dark:bg-dark-primary text-light-on-primary dark:text-dark-on-primary rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Registrazione in corso...' : 'Registrati'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-light-on-surface-variant dark:text-dark-on-surface-variant">
            Hai già un account?{' '}
            <Link to="/login" className="text-light-primary dark:text-dark-primary hover:underline">
              Accedi
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
